import React, { useMemo, useState, useCallback, useRef, useEffect } from "react";
import { Card, Modal, Button, Badge } from "react-bootstrap";
import { Column, DataGrid, Scrolling } from "devextreme-react/data-grid";
import { ConsumerOfferDetail, PaymentEntryItem, useSalesEntry } from "contexts/SalesEntryContext";
import Loader from "Common/Loader";
import { v4 as uuidv4 } from 'uuid';
import { toast } from "react-toastify";
import { GetAvailbaleSlabConsumerOffers } from "../services/api.services";
import { extractConsumerOfferDetails, extractConsumerOfferDetailsFromArray } from "./SalesEntryMain";
import notistack from "modules/core/notistack";

interface SummaryRowConfig {
  id: string;
  label: string;
  getValue: (state: any) => number | string;
  className?: string;
  isTotal?: boolean;
  format?: 'currency' | 'points' | 'text';
}

interface SummarySectionConfig {
  id: string;
  title: string;
  headerClass?: string;
  rows: SummaryRowConfig[];
}

export interface SlabItem {
  materialType: string;
  isSRP: boolean;
  styleNo: string;
  slabAmount: number;
  storeId: string;
}


export const SalesEntrySummary: React.FC = () => {
  const { state, actions } = useSalesEntry();
  const [showConsumerOfferModal, setShowConsumerOfferModal] = useState(false);
  const [isBulkLoading, setIsBulkLoading] = useState(false);

  // Add refs to prevent unnecessary re-renders
  const dataGridRef = useRef<any>(null);
  const modalStateRef = useRef({ isOpen: false });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const [refreshKey, setRefreshKey] = useState(0);
  const defaultsAppliedRef = useRef<Set<string>>(new Set());

  const handleAddToSlab = async () => {
    if (!state?.items || !storeId) return;

    const mapped: SlabItem[] = state.items.map((item) => ({
      materialType: item.materialTypeId,
      isSRP: item.isSRP,
      styleNo: item.styleNo,
      slabAmount: item.mrpPrice - (item?.totalConsumerDiscount || 0),
      storeId: storeId,
    }));

    if (mapped.length === 0) {
      return;
    }

    const res = await GetAvailbaleSlabConsumerOffers([...mapped]);
    const data: ConsumerOfferDetail[] = res.data;
    const offerDetails: ConsumerOfferDetail[] = extractConsumerOfferDetailsFromArray(data);

    // 1. Extract previously selected Invoice Range offers
    const previouslySelectedInvoiceOffers = (state.consumerOfferDetail || []).filter(
      x => x.discountTypeNmae === "Invoice Range" && x.isSelected
    );

    // 2. If there are any new Invoice Range offers
    if (offerDetails.length > 0) {
      const updatedInvoiceOffers = offerDetails.map((offer) => {
        // Check if this offer was previously selected
        const wasSelected = previouslySelectedInvoiceOffers.some(
          prev => prev.consumerOfferId === offer.consumerOfferId
        );
        return {
          ...offer,
          isSelected: wasSelected, // Preserve selection
        };
      });

      // 3. Replace only Invoice Range offers
      state.consumerOfferDetail = [
        ...(state.consumerOfferDetail?.filter(x => x.discountTypeNmae !== "Invoice Range") || []),
        ...updatedInvoiceOffers
      ];

      const InvoiceOffers = (state.consumerOfferDetail || []).filter(
        x => x.discountTypeNmae === "Invoice Range" && !x.isSelected && x.isDefault
      );
      await applyDefaultOffers(InvoiceOffers);

    } else {
      // 4. No invoice offers returned → remove all invoice offers
      state.consumerOfferDetail = state.consumerOfferDetail?.filter(
        x => x.discountTypeNmae !== "Invoice Range"
      ) || [];
    }

  };

  const applyDefaultOffers = useCallback(async (offers: ConsumerOfferDetail[]) => {
    const offerSetKey = offers
      .map(o => `${o.id}-${o.isDefault}`)
      .sort()
      .join('|');

    if (defaultsAppliedRef.current.has(offerSetKey)) {
      return;
    }

    const defaultOffers = offers.filter(
      offer => offer.isDefault && !offer.isSelected
    );

    if (defaultOffers.length === 0) return;

    actions.removeRewardPoints();
    actions.clearRewardRedeemDetails();
    if (state.discount != null && state.discount > 0) {
      notistack.warning('The discount has been removed as the applied consumer offer has changed. Please reapply the discount to continue.');
      actions.setDiscount(0);
    }
    if (state?.coupon && (!state?.coupon?.isConsumerOfferApplicable)) {
      toast.warning("This voucher code can't be clubbed with a consumer offer.");
      return;
    }

    defaultOffers.forEach((offer) => {
      const isClub = offer?.isClub == true || String(offer?.isClub) == "true";

      const selectedNonClubbableOffers = state?.consumerOfferDetail?.filter(o =>
        o.isSelected && (o.isClub === false || String(o.isClub) === "false")
      );

      if (!isClub && !offer?.isSelected && selectedNonClubbableOffers && selectedNonClubbableOffers.length > 0) {
        toast.warning("This offer can't be clubbed with another consumer offer.");
        return;
      }
      actions.toggleConsumerOfferSelection(offer.id);
    })

    defaultsAppliedRef.current.add(offerSetKey);

    if (defaultOffers.length > 0) {
      toast.info("Default offers applied successfully");
    }
  }, [actions]);

  // Memoize consumer offer details with stable reference
  const consumerOfferDetails = useMemo(() => {
    const offers = state?.consumerOfferDetail || [];
    const realOffers = offers.filter(offer =>
    (
      (offer.productId && !offer.productId.startsWith('test_') &&
        state?.items.some(item => item.id === offer.productId))
      || !offer.productId
    )
    );
    const selectedOffers = realOffers.filter(offer => offer.isSelected);
    const totalDiscount = selectedOffers.reduce((sum, offer) => sum + (offer.originalValue || 0), 0);
    // const roundedDiscount = Math.round(totalDiscount * 100) / 100;
    const roundedDiscount = Math.round(state.summary?.consumerOffer * 100) / 100;
    return {
      allOffers: realOffers,
      selectedOffers,
      totalDiscount,
      hasOffers: realOffers.length > 0,
      roundedDiscount
    };
  }, [state?.consumerOfferDetail, state?.items, refreshKey, handleAddToSlab,]);

  const storeId = localStorage.getItem('storedataid')
  const handleConsumerOfferClick = useCallback(() => {
    modalStateRef.current.isOpen = true;
    setShowConsumerOfferModal(true);
  }, []);

  useEffect(() => {
    handleAddToSlab()
  }, [state.items, handleConsumerOfferClick, handleAddToSlab]);


  useEffect(() => {
    if (consumerOfferDetails.allOffers.length > 0) {

      const hasAnySelectedOffer = consumerOfferDetails.allOffers.some(offer => offer.isSelected);
      if (hasAnySelectedOffer) {
        actions.removeRewardPoints()
        actions.clearRewardRedeemDetails();
        if (state.discount != null && state.discount > 0) {
          notistack.warning('The discount has been removed as the applied consumer offer has changed. Please reapply the discount to continue.')
          actions.setDiscount(0);
        }
        if (state?.coupon && (!state?.coupon?.isConsumerOfferApplicable)) {
          toast.warning("This voucher code can't be clubbed with a consumer offer.");
          return;
        }
      }

      const defaultOffers = consumerOfferDetails.allOffers.filter(offer => offer.isDefault && !offer.isSelected);

      defaultOffers.forEach((offer) => {
        const isClub = offer?.isClub == true || String(offer?.isClub) == "true";

        const selectedNonClubbableOffers = state?.consumerOfferDetail?.filter(o =>
          o.isSelected && (o.isClub === false || String(o.isClub) === "false")
        );

        if (!isClub && !offer?.isSelected && selectedNonClubbableOffers && selectedNonClubbableOffers.length > 0) {
          toast.warning("This offer can't be clubbed with another consumer offer.");
          return;
        }
        actions.toggleConsumerOfferSelection(offer.id);
      })

      if (defaultOffers.length > 0) {
        toast.info("Default offers applied successfully");
      }

      reloadDataSource();
    }
  }, [state.items.length]);

  const handleCloseModal = useCallback(() => {
    modalStateRef.current.isOpen = false;
    setShowConsumerOfferModal(false);
  }, []);

  const formatValue = (value: number | string, format?: string) => {
    if (value === null || value === undefined) {
      if (format === 'currency') {
        return formatCurrency(0);
      }
      if (format === 'points') {
        return '0 pts';
      }
      return '0';
    }

    if (format === 'currency' && typeof value === 'number') {
      return formatCurrency(value);
    }
    if (format === 'points' && typeof value === 'number') {
      return `${value} pts`;
    }
    return value.toString();
  };

  // Dynamic summary sections configuration
  const summaryConfig: SummarySectionConfig[] = useMemo(() => {
    const isIgst = state?.customer?.isIgst;

    const currencyRow = (
      id: string,
      label: string,
      getValue: (s: any) => number | string,
      className?: string
    ): SummaryRowConfig => ({
      id,
      label,
      getValue,
      format: 'currency',
      ...(className ? { className } : {})
    });

    const textRow = (
      id: string,
      label: string,
      getValue: (s: any) => number | string,
      className?: string
    ): SummaryRowConfig => ({
      id,
      label,
      getValue,
      format: 'text',
      ...(className ? { className } : {})
    });

    const sections: SummarySectionConfig[] = [
      {
        id: "summary",
        title: "Summary",
        headerClass: "bg-light",
        rows: [
          currencyRow("ornamentAmt", "Ornament Amt", (s: any) => s.summary?.ornamentAmt || 0),
          currencyRow("subAmt", "Sub Amt", (s: any) => s.summary?.subAmt || 0),
          currencyRow("consumerOffer", "Consumer Offer", (s: any) => {
            const value = s.summary?.consumerOffer;
            return typeof value === "number" && !isNaN(value)
              ? value.toFixed(2)
              : 0;
          }, "text-danger"),
          currencyRow("storeOffer", "Store Offer", (s: any) => s.summary?.storeOffer || 0, "text-danger"),
          // currencyRow("discountVoucher", "Discount Voucher", (s: any) => s.summary?.discountVoucher || 0, "text-danger"),
          currencyRow("totalOffer", "Total Offer", (s: any) => {
            const value = s.summary?.totalOffer;
            return typeof value === "number" && !isNaN(value)
              ? value.toFixed(2)
              : "0.00";
          }, "text-danger"),
          ...(!isIgst
            ? [
              currencyRow("cgst", "CGST (1.5%)", (s: any) => s.summary?.cgst || 0),
              currencyRow("sgst", "SGST (1.5%)", (s: any) => s.summary?.sgst || 0)
            ]
            : [
              currencyRow("igst", "IGST (3%)", (s: any) => s.summary?.cgst + s.summary?.sgst || 0)
            ]
          ),
          currencyRow(
            "totalRewardDiscount",
            "Total Redeem Amt",
            (s: any) =>
              s.items
                ? Math.round(
                  s.items.reduce(
                    (sum: number, item: any) => sum + Number(item.totalRewardDiscount || 0),
                    0
                  ) * 100
                ) / 100
                : 0
            , "text-danger"),
          currencyRow("totalAmt", "Total Amt", (s: any) => s.summary?.totalAmt || 0),
          currencyRow("totalDueAmt", "Total Due Amt", (s: any) => {
            let amount = s.summary?.totalDueAmt ?? 0;
            amount = Math.round(amount * 100) / 100;
            if (Object.is(amount, -0)) amount = 0;
            return amount < 0 ? 0 : Number(amount.toFixed(2));
          })
        ]
      }
    ];

    const hasPayments = (state?.payment?.length || 0) > 0;
    const hasOldGold = (state?.voucherDetails?.oldGoldAmt || 0) > 0;
    const hasReturnAmount = state?.exchangeSalesReturnData?.returnSalesOrderModel?.totalReturnSaleOrderAmount != null;

    if (hasPayments || hasOldGold || hasReturnAmount) {
      const paymentRows: SummaryRowConfig[] = [];

      state?.payment?.forEach((payment: any, index: number) => {
        paymentRows.push(
          currencyRow(`payment-${index}`, `${payment.method}`, () => payment.amount)
        );
      });

      if (hasOldGold) {
        paymentRows.push(
          currencyRow("oldGoldPayment", "Old Gold Exchange",
            (s: any) => s.voucherDetails?.oldGoldAmt || 0,
            "text-success"
          )
        );
      }

      paymentRows.push(
        currencyRow("totalPaid", "Total Paid Amt", (s: any) => {
          const cashPayments = s?.payment?.reduce((total: number, p: any) => total + p.amount, 0) || 0;
          const oldGoldAmt = s?.voucherDetails?.oldGoldAmt || 0;
          const returnAmount = s?.exchangeSalesReturnData?.returnSalesOrderModel?.totalReturnSaleOrderAmount || 0;
          const total = cashPayments + oldGoldAmt + returnAmount;
          return (Math.floor(total * 100) / 100);
        }, "text-success")
      );

      paymentRows.push(
        currencyRow("pendingDueAmt", "Excess Payment", (s: any) => {
          const amt = s.summary?.totalDueAmt || 0;
          return amt < 0 ? Math.abs(amt) : 0;
        }, "text-success")
      );

      sections.push({
        id: "paymentDetails",
        title: "Payment Details",
        headerClass: "bg-light",
        rows: paymentRows,
      });
    }

    sections.push(
      {
        id: "voucherDetails",
        title: "Voucher Details",
        headerClass: "bg-light",
        rows: [
          textRow("oldGoldAmt", "Old Gold Amt", (s: any) => s.voucherDetails?.oldGoldAmt?.toFixed(2) ? s.voucherDetails?.oldGoldAmt?.toFixed(2) : "0.00"),
          textRow("voucherAmt", "Exchange Amt", (s: any) =>
            s.exchangeSalesReturnData?.returnSalesOrderModel?.totalReturnSaleOrderAmount != null
              ? s.exchangeSalesReturnData?.returnSalesOrderModel?.totalReturnSaleOrderAmount?.toFixed(2)
              : "0.00"
          ),
          textRow("discountVoucher", "Discount Voucher", (s: any) =>
            s?.summary?.discountVoucher != null
              ? Number(s?.summary?.discountVoucher).toFixed(2)
              : '0.00'
          ),
        ],
      },
      {
        id: "customerDetails",
        title: "Customer Details",
        headerClass: "bg-light",
        rows: [
          textRow("creditAmt", "Credit Amt", (s: any) =>
            s?.customer?.creditAmount != null
              ? Number(s.customer.creditAmount).toFixed(2)
              : '0.00'
          ),
          textRow("rewardPoints", "Reward Points", (s: any) =>
            s?.customer?.rewardPoints != null
              ? Number(s.customer.rewardPoints).toFixed(2)
              : '0.00'
          ),
        ],
      },
      // {
      //   id: "discountVoucher",
      //   title: "Discount Voucher",
      //   headerClass: "bg-light",
      //   rows: [
      //     textRow("discountVoucher", "Discount Voucher", (s: any) =>
      //       s?.summary?.discountVoucher != null
      //         ? Number(s?.summary?.discountVoucher).toFixed(2)
      //         : '0.00'
      //     ),
      //   ],
      // }
    );

    return sections;
  }, [
    state?.payment,
    state?.voucherDetails?.oldGoldAmt,
    state?.customer?.isIgst,
    state?.exchangeSalesReturnData?.returnSalesOrderModel?.totalReturnSaleOrderAmount,
    state?.customer?.creditAmount
  ]);

  const renderSummaryRow = (row: SummaryRowConfig) => {
    const value = row?.getValue(state);
    const formattedValue = formatValue(value, row?.format);
    const rowClass = row?.isTotal ? "summary-row summary-total" : "summary-row";
    const labelClass = `summary-cell summary-label ${row?.className || ""}`;
    const valueClass = `summary-cell summary-value text-end d-flex justify-content-end align-items-end ${row?.className || ""}`;

    const isConsumerOffer = row?.id === "consumerOffer";
    const hasConsumerOffers = consumerOfferDetails.hasOffers;

    return (
      <div key={row?.id} className={rowClass}>
        <div className={`${labelClass} d-flex justify-content-between align-items-center`}>
          <span>{row?.label}</span>
          {isConsumerOffer && (
            <i
              className="fa fa-tags"
              onClick={handleConsumerOfferClick}
              aria-hidden="true"
              title="View Consumer Offer Details"
              style={{
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                fontSize: '1.1rem',
                color: hasConsumerOffers ? '#007bff' : '#6c757d',
                marginLeft: '8px'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.color = '#0056b3';
                e.currentTarget.style.transform = 'scale(1.2)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.color = hasConsumerOffers ? '#007bff' : '#6c757d';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            ></i>
          )}
        </div>
        <div className={`${valueClass}`}>
          <span>{formattedValue}</span>
        </div>
      </div>
    );
  };

  const renderPaymentRow = (payment: PaymentEntryItem, index: number) => {
    const formattedValue = formatValue(payment.amount, "currency");
    const label = `${payment.method}`;

    return (
      <div key={`payment-${index}`} className="summary-row">
        <div className="summary-cell summary-label text-success">{label}</div>
        <div className="summary-cell summary-value text-end d-flex justify-content-end align-items-end">
          <span className="text-success">{formattedValue}</span>
        </div>
      </div>
    );
  };

  const renderSummarySection = (section: SummarySectionConfig) => {
    const isPaymentSection = section.id === "paymentDetails";

    return (
      <Card
        key={section.id}
        className={`${section.id === "customerDetails" ? "" : "mb-2"} shadow-sm border-0`}
      >
        <div className="summary-table">
          <div className={`summary-header px-3 py-2 ${section.headerClass || "bg-light"}`}>
            <h6 className="mb-0 fw-semibold">{section.title}</h6>
          </div>
          <div className="summary-body">
            {isPaymentSection ? (
              <>
                {state?.payment?.map((payment, index) =>
                  renderPaymentRow(payment, index)
                )}
                {((state?.voucherDetails?.oldGoldAmt || 0) > 0) && (
                  <div key="oldGoldPayment" className="summary-row">
                    <div className="summary-cell summary-label text-success">Old Gold Exchange</div>
                    <div className="summary-cell summary-value text-end d-flex justify-content-end align-items-end">
                      <span className="text-success">{formatCurrency(state?.voucherDetails?.oldGoldAmt || 0)}</span>
                    </div>
                  </div>
                )}
                {section.rows
                  .filter(
                    (row) =>
                      row?.id.startsWith("total") || row?.id.includes("pending") || row?.id.includes("Due")
                  )
                  .map(renderSummaryRow)}
              </>
            ) : (
              section.rows.map(renderSummaryRow)
            )}
          </div>
        </div>
      </Card>
    );
  };

  const reloadDataSource = () => {
    setRefreshKey((prev) => prev + 1);
  };

  // Add this useEffect hook inside your NewSalesEntryContent component
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Check if there are any unsaved changes
      if (state?.items.length > 0 ||
        state?.customer?.id) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [state?.items, state?.customer, state?.payment, state?.oldGold]);

  const handleOfferToggle = useCallback((offerId: string) => {
    if (!modalStateRef.current.isOpen) return;
    const offer = state?.consumerOfferDetail?.find(o => o.id == offerId);
    actions.removeRewardPoints()
    actions.clearRewardRedeemDetails();
    if (state.discount != null && state.discount > 0) {
      notistack.warning('The discount has been removed as the applied consumer offer has changed. Please reapply the discount to continue.')
      actions.setDiscount(0);

    }
    try {
      if (state?.coupon && (!state?.coupon?.isConsumerOfferApplicable)) {
        toast.warning("This voucher code can't be clubbed with a consumer offer.");
        return;
      }

      const isClub = offer?.isClub == true || String(offer?.isClub) == "true";

      // Check if ANY non-clubbable offer is already selected
      const selectedNonClubbableOffers = state?.consumerOfferDetail?.filter(o =>
        o.isSelected && (o.isClub === false || String(o.isClub) === "false")
      );

      // If trying to SELECT a NON-clubbable offer while another non-clubbable is already selected
      if (!isClub && !offer?.isSelected && selectedNonClubbableOffers && selectedNonClubbableOffers.length > 0) {
        toast.warning("This offer can't be clubbed with another consumer offer.");
        return;
      }

      // Allow the action for all other cases
      actions.toggleConsumerOfferSelection(offerId);
      reloadDataSource()
    } catch (error) {
      console.error('Error toggling consumer offer:', error);
    }
  }, [actions, state]);


  // OPTIMIZED: Bulk operations with better state management
  const handleSelectAll = useCallback(() => {
    if (isBulkLoading) return;

    setIsBulkLoading(true);
    actions.removeRewardPoints()
    actions.clearRewardRedeemDetails();
    if (state.discount != null && state.discount > 0) {
      notistack.warning('The discount has been removed as the applied consumer offer has changed. Please reapply the discount to continue.')
      actions.setDiscount(0);

    }
    requestAnimationFrame(() => {
      try {
        // Check if any non-clubbable offer is already selected
        const selectedNonClubbableOffers = consumerOfferDetails.allOffers.filter(offer =>
          offer.isSelected && (offer.isClub === false || String(offer.isClub) === "false")
        );

        if (selectedNonClubbableOffers.length > 0) {
          toast.warning("Cannot select all while a non-clubbable offer is applied.");
          return;
        }

        // Apply all clubbable offers and only first non-clubbable offer
        let appliedNonClubbable = false;

        consumerOfferDetails.allOffers.forEach(offer => {
          if (state?.coupon && (!state?.coupon?.isConsumerOfferApplicable)) {
            return;
          }

          const isClub = offer?.isClub == true || String(offer?.isClub) == "true";

          // For non-clubbable offers: apply only the first one
          if (!isClub) {
            if (!appliedNonClubbable && !offer.isSelected) {
              actions.toggleConsumerOfferSelection(offer.id);
              appliedNonClubbable = true;
            }
            return;
          }

          // For clubbable offers: apply all
          if (!offer.isSelected) {
            actions.toggleConsumerOfferSelection(offer.id);
          }
        });

      } finally {
        setTimeout(() => setIsBulkLoading(false), 200);
      }
    });
  }, [consumerOfferDetails.allOffers, actions, isBulkLoading]);

  const handleUnselectAll = useCallback(() => {
    if (isBulkLoading) return;
    actions.removeRewardPoints()
    actions.clearRewardRedeemDetails();
    if (state.discount != null && state.discount > 0) {
      notistack.warning('The discount has been removed as the applied consumer offer has changed. Please reapply the discount to continue.')
      actions.setDiscount(0);
    }

    setIsBulkLoading(true);

    requestAnimationFrame(() => {
      try {
        consumerOfferDetails.allOffers.forEach(offer => {
          if (offer.isSelected) {
            actions.toggleConsumerOfferSelection(offer.id);
          }
        });
        reloadDataSource();
      } finally {
        setTimeout(() => setIsBulkLoading(false), 200);
      }
    });
  }, [consumerOfferDetails.allOffers, actions, isBulkLoading]);

  // OPTIMIZED: Stable memoized column components
  const SelectColumn = useMemo(() => (
    <Column
      caption="Select"
      width={80}
      alignment="center"
      cellRender={({ data }) => {
        const isChecked = Boolean(data.isSelected);
        return (
          <div className="text-center">
            <input
              type="checkbox"
              checked={isChecked}
              onChange={(e) => {
                e.stopPropagation();
                handleOfferToggle(data.id);
              }}
              className="form-check-input"
              style={{
                transform: 'scale(1.2)',
                cursor: 'pointer'
              }}
            />
          </div>
        );
      }}
    />
  ), [handleOfferToggle]);

  const PromotionColumn = useMemo(() => (
    <Column
      caption="Promotion Name"
      dataField="promotionName"
      width={200}
      cellRender={({ data }) => (
        <div style={{ opacity: data.isSelected ? 1 : 0.7 }}>
          <div className={`fw-bold ${data.isSelected ? 'text-primary' : ''}`}>
            {data.isSelected && <i className="fa fa-check-circle text-success me-1"></i>}
            {data.promotionName || data.offerName || 'Unnamed Promotion'}
          </div>
          <small className="text-muted">
            {data.isDefault && (
              <Badge bg="warning" className="text-dark me-2">
                Default
              </Badge>
            )}
            {data.isActive ? (
              <Badge bg="success">Active</Badge>
            ) : (
              <Badge bg="secondary">Inactive</Badge>
            )}
            {data.isSelected && (
              <Badge bg="primary" className="ms-2">Selected</Badge>
            )}
          </small>
        </div>
      )}
    />
  ), []);

  const DiscountTypeColumn = useMemo(() => (
    <Column
      caption="Discount Type"
      width={140}
      cellRender={({ data }) => (
        <div>
          <Badge bg="outline-primary" className="text-dark mb-1">
            {data.discountTypeNmae || data.offerType || 'Standard'}
          </Badge>
          <div className="small text-muted">
            {data.isPercentage ? 'Percentage' : 'Fixed Amount'}
          </div>
        </div>
      )}
    />
  ), []);

  const ProductInfoColumn = useMemo(() => (
    <Column
      caption="Product Info"
      width={180}
      cellRender={({ data }) => (
        <div className="small">
          {/* <div><strong>Style:</strong> {data.isStyleNo ? data.styleNumber : 'N/A'}</div> */}
          {/* <div><strong>Bag:</strong> {data.bagNo || 'N/A'}</div> */}
          {data.materialName && <div><strong>Material:</strong> {data.materialName}</div>}
        </div>
      )}
    />
  ), []);

  const ConsumerOfferOn = useMemo(() => (
    <Column
      caption="ConsumerOfferOn"
      width={180}
      cellRender={({ data }) => (
        <div className="small">
          {data.consumerOfferOn && <div>{data.consumerOfferOn}</div>}
        </div>
      )}
    />
  ), []);

  const DiscountValueColumn = useMemo(() => (
    <Column
      caption="Discount Value"
      dataField="discountValue"
      width={120}
      alignment="center"
      cellRender={({ data }) => (
        <div className="text-center">
          {data.isPercentage ? (
            <span className={`badge fs-6 ${data.isSelected ? 'bg-info' : 'bg-secondary'}`}>
              {data.discountValue || 0}%
            </span>
          ) : (
            <span className={`badge fs-6 ${data.isSelected ? 'bg-warning text-dark' : 'bg-secondary'}`}>
              ₹{formatCurrency(data.discountValue || 0)}
            </span>
          )}
          {data.isSelected && (
            <div className="small text-success mt-1">
              <i className="fa fa-check"></i> Applied
            </div>
          )}
        </div>
      )}
    />
  ), []);

  const ValidityColumn = useMemo(() => (
    <Column
      caption="Validity Period"
      width={150}
      cellRender={({ data }) => (
        <div className="small text-center">
          {data.fromDate && data.toDate ? (
            <div>
              <div><strong>From:</strong>  {new Date(data.fromDate).toLocaleDateString("en-GB")}</div>
              <div><strong>To:</strong>  {new Date(data.toDate).toLocaleDateString("en-GB")}</div>
            </div>
          ) : (
            <span className="text-muted">No validity period</span>
          )}
        </div>
      )}
    />
  ), []);

  const InvoiceRangeColumn = useMemo(() => (
    <Column
      caption="Invoice Range"
      width={120}
      alignment="center"
      cellRender={({ data }) => (
        <div className="small text-center">
          {data.invoiceRangeFrom > 0 || data.invoiceRangeTo > 0 ? (
            <div>
              <div>₹{formatCurrency(data.invoiceRangeFrom)}</div>
              <div>to ₹{formatCurrency(data.invoiceRangeTo)}</div>
            </div>
          ) : (
            <span className="text-muted">No Range</span>
          )}
        </div>
      )}
    />
  ), []);

  // OPTIMIZED: Memoized modal component with stable structure
  const ConsumerOfferModal = useMemo(() => (
    <Modal
      show={showConsumerOfferModal}
      onHide={handleCloseModal}
      size="xl"
      centered
      backdrop="static"
    >
      <Modal.Header closeButton className="bg-light">
        <div className="d-flex justify-content-between align-items-center w-100">
          <Modal.Title className="text-primary">
            <i className="fa fa-tags me-2"></i>
            Consumer Offer Details
          </Modal.Title>
          {consumerOfferDetails.hasOffers && (
            <div className="d-flex gap-2">
              <Button
                variant="outline-primary"
                size="sm"
                onClick={handleSelectAll}
                disabled={isBulkLoading || consumerOfferDetails.selectedOffers.length === consumerOfferDetails.allOffers.length}
              >
                <i className="fa fa-check-square me-1"></i>
                Select All
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={handleUnselectAll}
                disabled={isBulkLoading || consumerOfferDetails.selectedOffers.length === 0}
              >
                <i className="fa fa-square-o me-1"></i>
                Clear All
              </Button>
            </div>
          )}
        </div>
      </Modal.Header>
      <Modal.Body className="p-0">
        {consumerOfferDetails.hasOffers ? (
          <>
            <div className="p-3 border-bottom bg-light">
              <div className="row">
                <div className="col-md-4">
                  <div className="text-center">
                    <h6 className="text-muted mb-1">Total Offers</h6>
                    <Badge bg="info" className="fs-6">{consumerOfferDetails.allOffers.length}</Badge>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="text-center">
                    <h6 className="text-muted mb-1">Selected Offers</h6>
                    <Badge bg="success" className="fs-6">{consumerOfferDetails.selectedOffers.length}</Badge>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="text-center">
                    <h6 className="text-muted mb-1">Total Discount</h6>
                    <Badge bg="danger" className="fs-6">₹{formatCurrency(consumerOfferDetails.roundedDiscount)}</Badge>
                  </div>
                </div>
              </div>
            </div>

            <div style={{ height: '75%', padding: '10px' }}>
              {consumerOfferDetails.allOffers.length > 0 ? (
                <DataGrid
                  ref={dataGridRef}
                  dataSource={consumerOfferDetails.allOffers}
                  showBorders={true}
                  showColumnLines={true}
                  showRowLines={true}
                  allowColumnReordering={false}
                  columnAutoWidth={true}
                  rowAlternationEnabled={true}
                  keyExpr='id'
                  key={refreshKey}
                  noDataText="No consumer offers found"
                  repaintChangesOnly={true}
                  cacheEnabled={true}
                  hoverStateEnabled={true}
                  height="100%"
                  // KEY OPTIMIZATION: Prevent unnecessary re-renders
                  onContentReady={(e) => {
                    // Prevent excessive content ready events
                    if (e.component) {
                      e.component.option('loadPanel.enabled', false);
                    }
                  }}
                >
                  {SelectColumn}
                  {PromotionColumn}
                  {ConsumerOfferOn}
                  {DiscountTypeColumn}
                  {ProductInfoColumn}
                  {DiscountValueColumn}
                  {ValidityColumn}
                  {InvoiceRangeColumn}
                  <Scrolling
                    mode='standard'
                    useNative={true}
                    scrollByContent={true}
                    scrollByThumb={true}
                  />
                </DataGrid>
              ) : (
                <div className="text-center py-5">
                  <i className="fa fa-tags fa-3x text-muted mb-3"></i>
                  <h5 className="text-muted">No Consumer Offers Available</h5>
                  <p className="text-muted">No offers have been applied to the selected products.</p>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="text-center py-5">
            <i className="fa fa-tags fa-3x text-muted mb-3"></i>
            <h5 className="text-muted">No Consumer Offers Available</h5>
            <p className="text-muted">
              {state?.items.length === 0
                ? "Add products to your cart to see available consumer offers."
                : "No promotional offers are available for the selected products."
              }
            </p>
          </div>
        )}
      </Modal.Body>
    </Modal>
  ), [
    showConsumerOfferModal,
    handleCloseModal,
    consumerOfferDetails,
    isBulkLoading,
    handleSelectAll,
    handleUnselectAll,
    SelectColumn,
    PromotionColumn,
    DiscountTypeColumn,
    ProductInfoColumn,
    DiscountValueColumn,
    ValidityColumn,
    InvoiceRangeColumn,
    state?.items.length
  ]);

  return (
    <>
      {isBulkLoading && <Loader />}
      <div className="sales-entry-summary">
        {summaryConfig.map(renderSummarySection)}

        <style>{`
        .sales-entry-summary .summary-table {
          border: 1px solid #dee2e6;
          border-radius: 6px;
          overflow: hidden;
        }
        
        .sales-entry-summary .summary-header {
          border-bottom: 1px solid #dee2e6;
        }
        
        .sales-entry-summary .summary-body {
          background-color: #fff;
        }
        
        .sales-entry-summary .summary-row {
          display: flex;
          border-bottom: 1px solid #dee2e6;
        }
        
        .sales-entry-summary .summary-row:last-child {
          border-bottom: none;
        }
        
        .sales-entry-summary .summary-row?.summary-total {
          background-color: #f8f9fa;
          border-top: 2px solid #dee2e6;
        }
        
        .sales-entry-summary .summary-cell {
          padding: 7px 12px;
          display: flex;
          align-items: center;
          font-size: 12px;
        }
        
        .sales-entry-summary .summary-label {
          flex: 1;
          border-right: 1px solid #dee2e6;
          background-color: #fff;
        }
        
        .sales-entry-summary .summary-label.bg-light {
          background-color: #f8f9fa !important;
        }
        
        .sales-entry-summary .summary-value {
          min-width: 100px;
          font-weight: 500;
        }
        
        .sales-entry-summary .summary-total .summary-cell {
          font-weight: 600;
        }
      `}</style>
      </div>

      {ConsumerOfferModal}
    </>
  );
};